import { groupZipcodesForZipcodeTable, resolveZipcodeLngLat } from "util/zipcodeListGroup";
import { getTourMapColor } from "util/tourMapColor";

const STORAGE_KEY = "zipcodes-visualization-selected-tours";

export function isDeliveryTour(tour) {
  return (tour?.tour_type_category ?? tour?.tour_category) !== "pickup";
}

export function loadVisualizationTourSelection() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.map((id) => Number(id)).filter((id) => Number.isFinite(id));
  } catch {
    return [];
  }
}

export function saveVisualizationTourSelection(tourIds) {
  const ids = [...new Set(tourIds.map((id) => Number(id)).filter((id) => Number.isFinite(id)))];
  localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
}

export function zipMatchesSupplierFilter(zip, supplierIds) {
  if (!supplierIds?.length) {
    return false;
  }
  const targets = new Set(supplierIds.map((id) => Number(id)));
  const ids = zip.supplierIds || [];
  return ids.some((id) => targets.has(Number(id)));
}

export function filterTourGroupsBySupplier(tourGroups, supplierIds) {
  if (!supplierIds.length) {
    return tourGroups.map((tour) => ({ ...tour, zipcodes: [] }));
  }
  return tourGroups.map((tour) => ({
    ...tour,
    zipcodes: tour.zipcodes.filter((zip) => zipMatchesSupplierFilter(zip, supplierIds)),
  }));
}

export function allVisualizationSupplierIds(supplierOptions) {
  return (supplierOptions || []).map((s) => Number(s.id)).filter((id) => Number.isFinite(id));
}

export function buildVisualizationSupplierOptions(tourGroups, suppliers) {
  const nameById = new Map(
    (suppliers || []).map((s) => [Number(s.id), s.name ?? String(s.id)]),
  );
  const ids = new Set();
  for (const tour of tourGroups || []) {
    for (const zip of tour.zipcodes || []) {
      for (const sid of zip.supplierIds || []) {
        if (Number.isFinite(Number(sid))) {
          ids.add(Number(sid));
        }
      }
    }
  }
  return [...ids]
    .sort((a, b) => String(nameById.get(a) ?? a).localeCompare(String(nameById.get(b) ?? b)))
    .map((id) => ({
      id,
      name: nameById.get(id) ?? String(id),
    }));
}

export function buildZipcodeVisualizationByTour(zipcodes, deliveryTours) {
  const tourMeta = new Map(
    (deliveryTours || []).map((tour) => {
      const tourId = Number(tour.id);
      return [
        tourId,
        {
          tourId,
          tourName: tour.name ?? String(tour.id),
          color:
            typeof tour?.color === "string" && tour.color.trim()
              ? tour.color.trim()
              : getTourMapColor(tourId, tour.name),
        },
      ];
    }),
  );

  const byTour = new Map();
  const seenZipKeys = new Map(); // key -> zipcode entry

  for (const raw of zipcodes || []) {
    const z = String(raw.zipcode ?? "").trim();
    if (!z) {
      continue;
    }
    const country = raw.country;
    const supplierId = raw.supplier_id;
    const supplierName = raw.Supplier?.name ?? String(supplierId ?? "");
    const resolved = resolveZipcodeLngLat(raw);

    const timings = raw.ZipcodeDeliveryTimings || [];
    if (timings.length > 0) {
      for (const zdt of timings) {
        const tdt = zdt.ToursDeliveryTiming;
        if (!tdt) continue;
        const tourId = Number(tdt.tour_id);
        if (!Number.isFinite(tourId) || !tourMeta.has(tourId)) {
          continue;
        }

        const departure = tdt.delivery_timing || null;
        const uniqueKey = `${z}\0${country}\0${tourId}\0${departure}`.toLowerCase();

        if (seenZipKeys.has(uniqueKey)) {
          const entry = seenZipKeys.get(uniqueKey);
          if (supplierId && !entry.supplierIds.includes(supplierId)) {
            entry.supplierIds.push(supplierId);
            entry.supplierNames.push(supplierName);
            entry.supplierLabel = entry.supplierNames.join(", ");
          }
          if (raw.supplier_location_id && !entry.supplierLocationIds.includes(raw.supplier_location_id)) {
            entry.supplierLocationIds.push(raw.supplier_location_id);
          }
          continue;
        }

        if (!byTour.has(tourId)) {
          const meta = tourMeta.get(tourId);
          byTour.set(tourId, {
            tourId: meta.tourId,
            tourName: meta.tourName,
            color: meta.color,
            zipcodes: [],
          });
        }

        const tourName = tdt.Tour?.name || tourMeta.get(tourId).tourName;

        const newEntry = {
          id: `${raw.id}-${tourId}-${departure}`,
          zipcode: z,
          country,
          tourId,
          tourName,
          supplierLabel: supplierName,
          supplierIds: [supplierId].filter(Boolean),
          supplierNames: [supplierName].filter(Boolean),
          supplierLocationIds: [raw.supplier_location_id].filter(Boolean),
          latitude: resolved?.latitude ?? null,
          longitude: resolved?.longitude ?? null,
          hasCoordinates: resolved != null,
          color: tourMeta.get(tourId).color,
          departure: departure,
          departures: [departure].filter(Boolean),
          rawZipcodeId: raw.id,
        };

        byTour.get(tourId).zipcodes.push(newEntry);
        seenZipKeys.set(uniqueKey, newEntry);
      }
    } else {
      const tourId = Number(raw.tour_id);
      if (!Number.isFinite(tourId) || !tourMeta.has(tourId)) {
        continue;
      }
      const departure = raw.departure || null;
      const uniqueKey = `${z}\0${country}\0${tourId}\0${departure}`.toLowerCase();

      if (seenZipKeys.has(uniqueKey)) {
        const entry = seenZipKeys.get(uniqueKey);
        if (supplierId && !entry.supplierIds.includes(supplierId)) {
          entry.supplierIds.push(supplierId);
          entry.supplierNames.push(supplierName);
          entry.supplierLabel = entry.supplierNames.join(", ");
        }
        if (raw.supplier_location_id && !entry.supplierLocationIds.includes(raw.supplier_location_id)) {
          entry.supplierLocationIds.push(raw.supplier_location_id);
        }
        continue;
      }

      if (!byTour.has(tourId)) {
        const meta = tourMeta.get(tourId);
        byTour.set(tourId, {
          tourId: meta.tourId,
          tourName: meta.tourName,
          color: meta.color,
          zipcodes: [],
        });
      }

      const newEntry = {
        id: raw.id,
        zipcode: z,
        country,
        tourId,
        tourName: raw.Tour?.name || tourMeta.get(tourId).tourName,
        supplierLabel: supplierName,
        supplierIds: [supplierId].filter(Boolean),
        supplierNames: [supplierName].filter(Boolean),
        supplierLocationIds: [raw.supplier_location_id].filter(Boolean),
        latitude: resolved?.latitude ?? null,
        longitude: resolved?.longitude ?? null,
        hasCoordinates: resolved != null,
        color: tourMeta.get(tourId).color,
        departure: departure,
        departures: [departure].filter(Boolean),
        rawZipcodeId: raw.id,
      };

      byTour.get(tourId).zipcodes.push(newEntry);
      seenZipKeys.set(uniqueKey, newEntry);
    }
  }

  const tours = [...byTour.values()].sort((a, b) =>
    String(a.tourName).localeCompare(String(b.tourName)),
  );
  for (const tour of tours) {
    tour.zipcodes.sort((a, b) =>
      String(a.zipcode).localeCompare(String(b.zipcode), undefined, { numeric: true }),
    );
  }
  return tours;
}

export function countMappableZipcodesForTours(tourGroups, selectedTourIds) {
  const selected = new Set(selectedTourIds.map((id) => Number(id)));
  let mappable = 0;
  let total = 0;
  for (const tour of tourGroups) {
    if (!selected.has(Number(tour.tourId))) {
      continue;
    }
    for (const zip of tour.zipcodes) {
      total += 1;
      if (zip.hasCoordinates) {
        mappable += 1;
      }
    }
  }
  return { mappable, total };
}

function mapPointLocationKey(point) {
  const lat = Number(point.latitude).toFixed(6);
  const lng = Number(point.longitude).toFixed(6);
  return `${point.zipcode}|${point.country}|${lat}|${lng}`;
}

function toMapPointTourEntry(zip) {
  return {
    id: zip.id,
    zipcode: zip.zipcode,
    country: zip.country,
    tourId: zip.tourId,
    tourName: zip.tourName,
    departures: zip.departures || [],
    supplierLabel: zip.supplierLabel,
    latitude: zip.latitude,
    longitude: zip.longitude,
    hasCoordinates: zip.hasCoordinates,
    color: zip.tourColor || zip.color,
  };
}

export function mergeCollocatedMapPoints(points) {
  const groups = new Map();
  for (const point of points) {
    const key = mapPointLocationKey(point);
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key).push(point);
  }

  const merged = [];
  for (const members of groups.values()) {
    const tours = members.map(toMapPointTourEntry);
    const memberIds = members.map((m) => m.id);
    const first = members[0];

    if (members.length === 1) {
      merged.push({
        ...first,
        tours,
        memberIds,
        isMultiTour: false,
      });
      continue;
    }

    const uniqueSuppliers = [...new Set(tours.map((t) => t.supplierLabel).filter(Boolean))];
    const supplierLabel = uniqueSuppliers.join(", ") || "";

    merged.push({
      id: `merged-${first.zipcode}-${first.latitude}-${first.longitude}`,
      zipcode: first.zipcode,
      country: first.country,
      latitude: first.latitude,
      longitude: first.longitude,
      hasCoordinates: true,
      tours,
      memberIds,
      isMultiTour: true,
      tourColor: tours[0]?.color,
      supplierLabel,
    });
  }

  return merged;
}

export function isMultiTourMapPoint(point) {
  return Boolean(point?.isMultiTour && point.tours?.length > 1);
}

export function isMapPointHighlighted(point, hoveredId, selectedId) {
  if (!point) {
    return false;
  }
  if (hoveredId === point.id || selectedId === point.id) {
    return true;
  }
  const members = point.memberIds;
  if (!members?.length) {
    return false;
  }
  return members.includes(hoveredId) || members.includes(selectedId);
}

export function isSidebarZipHighlighted(zip, hoveredId, mapPoints) {
  if (!hoveredId || !zip) {
    return false;
  }
  if (hoveredId === zip.id) {
    return true;
  }
  for (const point of mapPoints || []) {
    if (point.id !== hoveredId) {
      continue;
    }
    return (point.memberIds || []).includes(zip.id);
  }
  return false;
}

export function mapPointToDetailSelection(point) {
  if (!point) {
    return null;
  }
  if (isMultiTourMapPoint(point)) {
    return {
      id: point.id,
      zipcode: point.zipcode,
      country: point.country,
      latitude: point.latitude,
      longitude: point.longitude,
      hasCoordinates: point.hasCoordinates,
      isMultiTour: true,
      tours: point.tours,
      supplierLabel: point.supplierLabel,
    };
  }
  const tour = point.tours?.[0];
  if (tour) {
    return {
      id: tour.id,
      zipcode: tour.zipcode,
      country: tour.country,
      tourId: tour.tourId,
      tourName: tour.tourName,
      departures: tour.departures || [],
      supplierLabel: tour.supplierLabel,
      latitude: tour.latitude,
      longitude: tour.longitude,
      hasCoordinates: tour.hasCoordinates,
      isMultiTour: false,
    };
  }
  return point;
}

export function buildMapPointsForTours(tourGroups, selectedTourIds) {
  const selected = new Set(selectedTourIds.map((id) => Number(id)));
  const points = [];
  for (const tour of tourGroups) {
    if (!selected.has(Number(tour.tourId))) {
      continue;
    }
    for (const zip of tour.zipcodes) {
      if (!zip.hasCoordinates) {
        continue;
      }
      points.push({
        ...zip,
        tourColor: tour.color,
      });
    }
  }
  return mergeCollocatedMapPoints(points);
}
